import { Button , Sheet, SheetContent , Wordmark } from "@roberttamas/ui";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation } from "react-router-dom";

import { AppSidebar } from "@/components/app-sidebar";
import { Menu } from "@/components/icons";

/**
 * Used both as a router layout route (children come from `<Outlet/>`)
 * and, occasionally, mounted directly by a page that needs the shell
 * without being nested under it in the route tree (e.g. `/buy`, which
 * must also work chrome-less for a signed-out landing-page visitor) —
 * pass `children` explicitly for that case.
 */
export function AppShell({ children }: { children?: ReactNode } = {}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();

  // Close the mobile drawer on navigation (covers back/forward too).
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen">
      {/* Desktop: fixed left sidebar. */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-72 border-r bg-background supports-[backdrop-filter]:bg-background/70 supports-[backdrop-filter]:backdrop-blur-xl lg:block">
        <AppSidebar />
      </aside>

      {/* Mobile: slide-in drawer. */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent className="p-0" title={t("nav.navigation")}>
          <AppSidebar onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="lg:pl-72">
        {/* Mobile top bar with hamburger. Hidden on desktop. */}
        <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b bg-background/80 px-3 backdrop-blur-xl lg:hidden">
          <Button
            variant="ghost"
            size="icon"
            aria-label={t("nav.openNavigation")}
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          <Wordmark strong="raw" rest=".gallery" className="text-3xl" />
        </header>

        <main className="mx-auto max-w-6xl px-4 py-8">
          <Button asChild className="sr-only">
            <a href="#main">{t("nav.skipToContent")}</a>
          </Button>
          <div id="main">
            {children ?? <Outlet />}
          </div>
        </main>
      </div>
    </div>
  );
}

